import { Grid, Checkbox } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  Button,
  DottedText,
  Download,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React, { CSSProperties } from 'react';
import componentConstants from '../messages/componentConstants';
import clsx from 'clsx';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { ChevronRight } from '@mui/icons-material';
import SvgSearchDB from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Icons/SearchDB';
import { getTruncatedText } from '../../pages/MemberSummaryPage';

// Download buttons related functions and data
interface IDownloadButtonProps {
  variant: 'outlined' | 'text' | 'contained' | undefined;
  style: CSSProperties;
  startIcon: React.JSX.Element;
}

const DownloadButtonsProps: IDownloadButtonProps = {
  variant: 'outlined',
  style: {
    padding: '10px 12px',
    height: '35px',
    border: `1px solid ${patientAccessTheme.palette.primary[500]}`,
    borderRadius: '4px',
    textTransform: 'none',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
  },
  startIcon: (
    <div style={{ height: '16px', width: '16px' }}>
      <Download height={'16px'} width={'16px'} />
    </div>
  ),
};

interface IDownloadButtonsInReportsProps {
  showDownloadSelected: boolean;
  handleDownloadAll: () => any;
  handleDowloadSelected: () => any;
}

export const DownloadButtons: React.FC<IDownloadButtonsInReportsProps> = ({
  showDownloadSelected,
  handleDowloadSelected,
  handleDownloadAll,
}) => {
  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
      }}
    >
      <Grid
        style={{
          marginRight: '12px',
          display: showDownloadSelected ? 'initial' : 'none',
        }}
      >
        <Button
          width="178px"
          label={componentConstants.PAGES.REPORTS.DOWNLOAD_SELECTED}
          onClick={handleDowloadSelected}
          {...DownloadButtonsProps}
        />
      </Grid>
      <Grid>
        <Button
          width="140px"
          label={componentConstants.PAGES.REPORTS.DOWNLOAD_ALL}
          onClick={handleDownloadAll}
          {...DownloadButtonsProps}
        />
      </Grid>
    </Grid>
  );
};

// Table related functions and data
export const getReportsTableColumns: any = (
  isQuarterly?: boolean,
  setOpenQuaterlyReport?: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const { classes } = makeStyles()({
    tableHeaderText: {
      fontWeight: 500,
      color: patientAccessTheme.palette.text.highemphasis,
      fontSize: '12px',
      lineHeight: '12px',
    },
    tableFirstColumn: {
      width: 'max-content',
    },
    tableCellText: {
      fontWeight: 400,
      color: patientAccessTheme.palette.text.mediumemphasis,
      fontSize: '14px',
      lineHeight: '24px',
    },
    subRows: {
      backgroundColor: patientAccessTheme.palette.grey[100],
      '&:first-child': {
        paddingLeft: '54px',
      },
    },
    checkboxChecked: {
      color: `${patientAccessTheme.palette.primary[500]} !important`,
    },
  })();
  return [
    {
      sequenceNo: 0,
      expansion: true,
      Header: (
        <Typography
          className={clsx(classes.tableFirstColumn, classes.tableHeaderText)}
          style={{
            width: '260px',
            paddingLeft: '16px',
          }}
        >
          {'Report Name'}
        </Typography>
      ),
      Footer: 'Report Name',
      accessor: 'reportName',
      cellType: 'none',
      icon: 'none',
      Cell: ({ row, value }: any) => (
        <Grid
          container
          style={{
            width: isQuarterly ? '260px' : '270px',
            paddingLeft: '16px',
          }}
          sx={{
            alignItems: 'center',
          }}
        >
          {row.canExpand && (
            <Grid
              style={{
                height: '24px',
                marginRight: '12px',
                transform: row.isExpanded ? 'rotate(90deg)' : 'none',
              }}
            >
              <ChevronRight
                style={{
                  height: '24px',
                  width: '24px',
                  color: patientAccessTheme.palette.primary[500],
                  cursor: 'pointer',
                }}
              />
            </Grid>
          )}
          {row.depth ? (
            <Grid style={{ marginRight: '20px' }}>
              <Checkbox
                onClick={() => {}}
                {...row.getToggleRowSelectedProps({ title: undefined })}
                disabled={false}
                style={{
                  color: patientAccessTheme.palette.checkbox?.border1,
                  height: '20px',
                  width: '20px',
                  borderRadius: '2px',
                }}
                classes={{
                  checked: classes.checkboxChecked,
                }}
              />
            </Grid>
          ) : (
            <></>
          )}
          {isQuarterly ? (
            <div
              onClick={() =>
                setOpenQuaterlyReport && setOpenQuaterlyReport(true)
              }
              style={{ fontWeight: 500 }}
            >
              <DottedText text={value} />
            </div>
          ) : (
            <Grid style={{ paddingLeft: false && row?.depth ? '44px' : '0px' }}>
              <Typography
                fontType="medium"
                className={clsx(
                  classes.tableFirstColumn,
                  classes.tableCellText,
                )}
              >
                {value}
              </Typography>
            </Grid>
          )}
        </Grid>
      ),
    },
    {
      sequenceNo: 1,
      Header: (
        <Typography
          className={classes.tableHeaderText}
          style={{ width: '600px' }}
        >
          {'Description'}
        </Typography>
      ),
      Footer: 'Description',
      accessor: 'description',
      cellType: 'none',
      icon: 'none',
      Cell: ({ value }: any) => (
        <Typography
          style={{ width: '600px' }}
          className={classes.tableCellText}
        >
          {getTruncatedText(value, '540px')}
        </Typography>
      ),
    },
    {
      sequenceNo: 2,
      Header: (
        <Typography
          className={classes.tableHeaderText}
          style={{ width: '171px', paddingLeft: '12px' }}
        >
          {'Last Updated'}
        </Typography>
      ),
      Footer: 'Last Updated',
      accessor: 'lastUpdated',
      cellType: 'none',
      icon: 'none',
      Cell: ({ value }: any) => (
        <Typography
          style={{ width: '171px', paddingLeft: '12px' }}
          className={classes.tableCellText}
        >
          {value}
        </Typography>
      ),
    },
    // {
    //   sequenceNo: 3,
    //   Header: (
    //     <Typography
    //       className={classes.tableHeaderText}
    //       style={{ width: '325px' }}
    //     >
    //       {'Download Frequency'}
    //     </Typography>
    //   ),
    //   Footer: 'Download Frequency',
    //   accessor: 'downloadFrequency',
    //   cellType: 'none',
    //   icon: 'none',
    //   Cell: ({ value }: any) => (
    //     <Typography
    //       style={{ width: '325px' }}
    //       className={classes.tableCellText}
    //     >
    //       {value}
    //     </Typography>
    //   ),
    // },
  ];
};

export const REPORTS_TABLE_DATA = [
  {
    id: 'summaryGroup',
    reportName: 'State Of Health',
    description:
      'Section providers high level overview of plan costs and drivers that have impacted cost. Also includes reports on disease prevalence, disease prognosis and care quality benchmark performance',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Annual',
    subRows: [
      {
        id: 'summaryReport',
        reportName: 'Executive Summary',
        description:
          'High level overview of plan costs and the drivers that have impacted cost. Also includes benchmarks on overall care quality and future risk.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Annual',
      },
    ],
  },
  {
    id: 'diseaseManagementGroup',
    reportName: 'Disease Management',
    description:
      'Focuses on insights into the prevalence, progression, and management of diseases within a population, aiming to identify, predict, and evaluate care for chronic conditions and disease risks.',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Annual',
    subRows: [
      {
        id: 'diseasePrevalenceReport',
        reportName: 'Disease and Condition Spend',
        description:
          'Cost and utilization patterns across diagnoses, conditions, and encounters—highlighting major cost drivers and opportunities for targeted health interventions—using ICD classification from the National Center for Health Statistics (NCHS).',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      {
        id: 'chronicPrevalenceReport',
        reportName: 'Chronic Prevalence',
        description:
          'Understand the potential relationships between conditions and their effects on your population to mitigate multi-morbidity risks.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      // {
      //   id: 'riskReport',
      //   reportName: 'Disease Prognosis',
      //   description:
      //     'Members on an intensifying disease progression, who may have a diagnosis in the next 12-24 months, and those members already with a diagnosis. A focus on disease prognosis can prevent the onset of disease and the costly treatments associated with it.',
      //   lastUpdated: '02-02-2022',
      //   downloadFrequency: 'Annual',
      // },
      {
        id: 'careQualityReport',
        reportName: 'Care Gaps',
        description:
          'Preventative care services can detect conditions in early stages or prevent the onset completely. Recommended services are highlighted here, along with the rate at with members are completing them.  These service guidelines and benchmarks are derived from Medicare and Healthcare Effectiveness Data and Information Set (HEDIS).',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Annual',
      },
    ],
  },
  {
    id: 'costManagementGroup',
    reportName: 'Cost Management',
    description:
      'Analyzes and optimizes healthcare costs associated with medical episodes, identifies wasteful spending, and spotlights high-cost claimants to enhance financial efficiency.',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Month, Quarter, Half-Year or Annual',
    subRows: [
      {
        id: 'episodesOfCareReport',
        reportName: 'Episodes of Care',
        description:
          'Explore the services and cost associated with certain planned procedures, including the distribution between pre-operative, operative, and post-operative periods',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      // {
      //   id: 'wasteCalculatorReport',
      //   reportName: 'Waste Calculator',
      //   description:
      //     'Identify potential waste in healthcare spending and help healthcare organizations prioritize interventions to improve the quality and efficiency of care',
      //   lastUpdated: '02-02-2022',
      //   downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      // },
      {
        id: 'highCostReport',
        reportName: 'High Cost Claimants',
        description:
          'High-cost claimants, though a small percentage of the population, can make up a large portion of the plan spend.  Members with medical claims greater than $10,000 and/or pharmacy claims greater than $5,000 are highlighted here.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
    ],
  },
  {
    id: 'stopLossManagementGroup',
    reportName: 'Stop Loss Management',
    description:
      'Manages and mitigates financial risks associated with stop loss insurance, focusing on identifying conditions that trigger stop loss claims, analyzing deductible levels, and evaluating financial impact.',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Month, Quarter, Half-Year or Annual',
    subRows: [
      {
        id: 'stoplossAggregateReport',
        reportName: 'Stop Loss Aggregate Report',
        description:
          'Detailed Monthly Breakdown of Paid Claims, Coverage Limits, and Enrollee Statistics.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Annual',
      },
      {
        id: 'stopLossReport',
        reportName: 'Stop Loss Triggers',
        description:
          'Certain conditions, as identified by the Self Insurance Institute of America (SIIA), have a higher instance of impacting stop loss payments, and ultimately plan premiums. The prevalence of each condition and the associate costs are displayed.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      {
        id: 'stoploss5050Report',
        reportName: '50-50 Report Plan',
        description:
          'Plan members, whose costs have crossed certain thresholds of the plan’s identified stop loss deductible.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
    ],
  },
  {
    id: 'planAnalysisGroup',
    reportName: 'Plan Analysis',
    description:
      "Provides a comprehensive analysis of the healthcare plan's performance, including claims cost distribution, enrollment metrics, and the effect of network usage on expenses.",
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Month, Quarter, Half-Year or Annual',
    subRows: [
      {
        id: 'planPerformanceReport',
        reportName: 'Summary',
        description:
          'Key indicators of the financial health of your health plan are summarized.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      {
        id: 'claimsReport',
        reportName: 'Claims Metrics',
        description:
          'A detailed accounting of the claims processed for plan members and a summary of the impact to the plan.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      {
        id: 'enrollmentReport',
        reportName: 'Enrollment Metrics',
        description:
          'Demographics of members and the plan types which they have elected.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      // {
      //   id: 'networkLeakageReport',
      //   reportName: 'Network Leakage',
      //   description:
      //     'A comparison of claims and cost distribution by in-network and out-of-network providers.',
      //   lastUpdated: '02-02-2022',
      //   downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      // },
    ],
  },
  {
    id: 'claimsManagementGroup',
    reportName: 'Claims Management',
    description:
      'Dedicated to the operational aspect of managing healthcare claims, aiming to improve the efficiency of claims processing and reduce administrative costs.',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Month, Quarter, Half-Year or Annual',
    subRows: [
      {
        id: 'rejectedClaimsReport',
        reportName: 'Rejected Claims',
        description:
          'A detailed accounting of the claims rejected by the plan or not yet processed.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      // {
      //   id: 'pendingClaimsReport',
      //   reportName: 'Pending Claims',
      //   description:
      //     'A detailed accounting of the claims pending by the plan or not yet processed.',
      //   lastUpdated: '02-02-2022',
      //   downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      // },
    ],
  },
  {
    id: 'medicalClaimUtilizationGroup',
    reportName: 'Medical Claim Management',
    description:
      'Focuses on the utilization of medical services, aiming to understand patterns in service utilization, identify cost reduction areas, and ensure appropriate use of resources.',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Month, Quarter, Half-Year or Annual',
    subRows: [
      {
        id: 'utilizationPatientReport',
        reportName: 'Admission Utilization',
        description:
          'Metrics on medical claims from plan members experiencing hospital admissions.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      // {
      //   id: 'utilizationPharmacyReport',
      //   reportName: 'Pharmacy Utilization',
      //   description:
      //     'Metrics on pharmacy spend and the drug types with the highest plan impact.',
      //   lastUpdated: '02-02-2022',
      //   downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      // },
      {
        id: 'utilizationProcedureReport',
        reportName: 'Procedure Utilization',
        description:
          'Metrics on procedure spend and the procedure types with the highest plan impact.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
      {
        id: 'utilizationProviderReport',
        reportName: 'Provider Utilization',
        description:
          'A listing of the top facilities and physicians utilized by plan members.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
    ],
  },
  {
    id: 'drugUtilizationGroup',
    reportName: 'Drug Utilization',
    description:
      'Analyzes spending and trends in drug utilization, aiming to provide a detailed overview of pharmaceutical expenditures and optimize drug spending. Edit Duration',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Month, Quarter, Half-Year or Annual',
    subRows: [
      {
        id: 'utilizationPharmacyReport',
        reportName: 'Drug Utilization Summary',
        description:
          'Metrics on pharmacy spend and the drug types with the highest plan impact.',
        lastUpdated: '02-02-2022',
        downloadFrequency: 'Month, Quarter, Half-Year or Annual',
      },
    ],
  },
];

export const CUSTOM_REPORT_TABLE_DATA = [
  {
    id: 'quaterlyReport',
    reportName: 'Quarterly Report',
    description:
      'Section providers high level overview of plan costs and drivers that have impacted cost. Also includes reports on disease prevalence, disease prognosis and care quality benchmark performance',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Quarter, Half-Year or Annual',
  },
];

export const REGULATORY_REPORTS_TABLE_DATA = [
  {
    id: 'caa',
    reportName: 'CAA',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Annual',
  },
  {
    id: 'stateMandated',
    reportName: 'State Mandated',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Annual',
  },
  {
    id: 'regulatory',
    reportName: 'Regulatory',
    lastUpdated: '02-02-2022',
    downloadFrequency: 'Annual',
  },
];

// Other helper functions
export const getTitle = (title: string) => {
  const boldTypographyProps = {
    fontSize: '18px',
    lineHeight: '22px',
    fontType: 'semiBold',
    fontWeight: 600,
    textColor: patientAccessTheme.palette.text.highemphasis,
  };

  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
      }}
    >
      <Typography {...boldTypographyProps}>Reports</Typography>
      <Typography
        fontSize="16px"
        lineHeight="26px"
        textColor={patientAccessTheme.palette.text.mediumemphasis}
        fontType="medium"
        margin="0px 8px"
      >
        for
      </Typography>
      <Typography {...boldTypographyProps}>{title}</Typography>
    </Grid>
  );
};

interface IOnHoverRowComponentProps {
   
  handleDownloadReport: () => any;
  handleQueryData?: () => any;
}

export const OnHoverRowComponent: React.FC<IOnHoverRowComponentProps> = ({
  handleDownloadReport,
}) => {
  const { classes } = makeStyles()({
    root: {
      width: 'fit-content',
      position: 'absolute',
      top: '16px',
      right: '8px',
    },
    onHoverIcon: {
      height: '20px',
      width: '20px',
      marginRight: '12px',
      cursor: 'pointer',
    },
  })();

  return (
    <Grid container className={classes.root}>
      <Grid className={classes.onHoverIcon} onClick={handleDownloadReport}>
        <Download
          stroke={patientAccessTheme.palette.status.icon}
          height="20px"
          width="20px"
        />
      </Grid>
      <Grid className={classes.onHoverIcon} style={{ visibility: 'hidden' }}>
        <SvgSearchDB height={'20px'} width={'20px'} />
      </Grid>
    </Grid>
  );
};

export const NEW_COMPARISON_TABLEDATA = [
  {
    documents: [
      {
        _id: '676089942ca234293044e719',
        document_name: 'Meritain Proposal for MedQuest Associates 8_8_2024.PDF',
        document_type: 'Base Document',
        document_url:
          'https://storageblobprod.blob.core.windows.net/contract-dev/contracts/MedQuest%20Associates/Meritain%20Proposal%20for%20MedQuest%20Associates%208_8_2024.PDF',
        document_effective_date: '01-01-2025',
        status: 'Identified',
      },
    ],
    contract_id: '676089e02ca234293044e739',
    effective_date: '01-01-2025',
    total_files: 1,
    type: 'Base Document',
    merge_status: 'Merged',
    is_document_converted: true,
    status: 'Processing',
    eg_nid: 'medquest_associates_1734379900955',
    health_care_partner_name: 'MedQuest Associates',
    service_agreement_name: 'Claims Administrator',
    reports: [
      'Compensation Transparency Compliance Check',
      'Gag Clause Prohibition Compliance Check',
      'Comparison with Stop Loss Agreement',
      'Commercial Evaluation',
    ],
    features: ['Obligations', 'Fees', 'Provision Matrix'],
    employer_info: [
      {
        _id: '6760897e2ca234293044e6f6',
        eg_nid: 'medquest_associates_1734379900955',
        employer_name: 'MedQuest Associates',
        tpa: 'MedQuest Associates',
        pbm: '',
        stop_loss_carrier: 'HCC Life Insurance Company',
        advisor_or_broker: 'Ironwood Benefits Advisory Services, LLC',
        network: '',
        status: 'Ready',
        createdBy: 'roshini@homahealth.us',
        createdAt: '2024-12-16T20:11:42.188Z',
        updatedAt: '2025-03-19T07:31:39.686Z',
        __v: 0,
      },
    ],
  },
  {
    documents: [
      {
        _id: '6756f6812fa2d36fbee89f17',
        document_name: 'Halo ASO BPA 9.1.2023-combined.pdf',
        document_type: 'Base Document',
        document_url:
          'https://storageblobprod.blob.core.windows.net/contract-dev/contracts/Image%20PDFs/Halo%20ASO%20BPA%209.1.2023-combined.pdf',
        document_effective_date: '09-01-2023',
        status: 'Identified',
      },
      {
        _id: '6756f6812fa2d36fbee89f18',
        document_name: 'Halo ASO BPA 9.1.2023-combined.pdf',
        document_type: 'Base Document',
        document_url:
          'https://storageblobprod.blob.core.windows.net/contract-dev/contracts/Image%20PDFs/Halo%20ASO%20BPA%209.1.2023-combined.pdf',
        document_effective_date: '09-01-2023',
        status: 'Identified',
      },
    ],
    contract_id: '6756f7462fa2d36fbee89f21',
    effective_date: '09-01-2023',
    total_files: 1,
    type: 'Base Document',
    merge_status: 'Merged',
    is_document_converted: true,
    status: 'Processing',
    eg_nid: 'image_pdfs_1733750006851',
    health_care_partner_name: 'Blue Cross and Blue Shield of Illinois',
    service_agreement_name: 'Claims Administrator',
    reports: [
      'Compensation Transparency Compliance Check',
      'Gag Clause Prohibition Compliance Check',
      'Comparison with Stop Loss Agreement',
      'Commercial Evaluation',
    ],
    features: ['Obligations', 'Fees', 'Provision Matrix'],
    employer_info: [
      {
        _id: '6756ecf82fa2d36fbee89ca1',
        eg_nid: 'image_pdfs_1733750006851',
        employer_name: 'Image PDFs',
        tpa: 'Blue Cross and Blue Shield of Illinois',
        pbm: '',
        stop_loss_carrier: 'Nationwide Life Insurance Company',
        advisor_or_broker: '',
        network: '',
        status: 'Ready',
        createdBy: 'avinash@homahealth.us',
        createdAt: '2024-12-09T13:13:28.203Z',
        updatedAt: '2025-03-19T07:02:28.628Z',
        __v: 0,
      },
    ],
  },
];
